import {CameraControllerOrientation} from "./camera/controllers/orientation/cameraControllerOrientation.js";
import {CameraControllerFinish} from "./camera/controllers/finish/cameraControllerFinish.js";
import * as THREE from "three";
import {CameraControllerLeaderboard} from "./camera/controllers/leaderboard/cameraControllerLeaderboard.js";
import {CameraControllerOrbit} from "./camera/cameraControllerOrbit.js";
import {CameraControllerCinematic} from "./camera/controllers/cinematic/cameraControllerCinematic.js";
import {CameraControllerGenerator} from "./camera/controllers/cinematic/generator/cameraControllerGenerator.js";
import {CameraControllerChase} from "./camera/controllers/chase/cameraControllerChase.js";
import {CameraAnchorRails} from "./camera/controllers/cinematic/anchors/cameraAnchorRails.js";
import {Random} from "./math/random.js";
import {CameraControllerIntroduction} from "./camera/controllers/introduction/cameraControllerIntroduction.js";
import {CameraControllerTransition} from "./camera/controllers/transition/cameraControllerTransition.js";
import {CameraMode} from "./camera/cameraMode.js";
import {Interpolation} from "./math/interpolation.js";

export class RaceCameras {
    static CAMERA_INTRODUCTION_SPEED = 6;
    static CAMERA_TRANSITION_SPEED = 1;

    /**
     * Construct the race cameras
     * @param {Camera} camera The camera to control
     * @param {Environment} environment The environment
     * @param {Track} track The track
     * @param {HTMLElement} element The element to listen for input on
     * @param {Racers} racers The racers, or null if this is a track builder
     */
    constructor(camera, environment, track, element, racers) {
        const random = new Random();

        this.ended = false;
        this.intro = true; // TODO: Enum this
        this.racers = racers;
        this.track = track;
        this.environment = environment;
        this.cameraIndex = 0;
        this.cameraStart = new CameraControllerOrientation(camera, environment);
        this.cameraFinish = new CameraControllerFinish(
            camera,
            new THREE.Vector3(8, 8, 0),
            new THREE.Vector3(),
            20);
        this.cameraLeaderboard = new CameraControllerLeaderboard(
            camera,
            track,
            environment);
        this.cameraOrbit = racers ? null : new CameraControllerOrbit(camera, element);
        this.cameraControllers = [
            new CameraControllerCinematic(
                camera,
                new CameraControllerGenerator(track, environment.planetPosition, random).generate()),
            new CameraControllerChase(camera),
            new CameraControllerCinematic(
                camera,
                [
                    new CameraAnchorRails(track, new THREE.Vector3(10, 40, 10), false)
                ],
                false),
            new CameraControllerOrientation(camera, environment)];

        this.cameraControllerIntroduction = new CameraControllerIntroduction(camera, racers);
        this.cameraControllerTransition = new CameraControllerTransition(camera, this.cameraControllerIntroduction, this.cameraControllers[0]);
        this.cameraTransition = this.cameraTransitionPrevious = 0;
        this.cameraMode = CameraMode.NONE;
        this.onEndIntro = null;

        this.addInputListeners();
    }

    /**
     * Update the state
     * @param {number} delta The time delta
     */
    update(delta) {
        this.cameraTransitionPrevious = this.cameraTransition;

        switch (this.cameraMode) {
            case CameraMode.INTRO:
                if ((this.cameraTransition += delta / RaceCameras.CAMERA_INTRODUCTION_SPEED) > 1) {
                    this.cameraTransition = this.cameraTransitionPrevious = 0;

                    this.cameraMode = CameraMode.TRANSITION;
                }

                break;

            case CameraMode.TRANSITION:
                if ((this.cameraTransition += delta / RaceCameras.CAMERA_TRANSITION_SPEED) > 1) {
                    this.onEndIntro();

                    this.cameraMode = CameraMode.GAME;
                }

                break;
        }

        this.cameraOrbit?.update();

        if (this.racers)
            this.cameraController.update(delta);
    }

    /**
     * Render
     * @param {number} time The time interpolation in the range [0, 1]
     */
    render(time) {
        switch (this.cameraMode) {
            case CameraMode.INTRO:
                this.cameraControllerIntroduction.setTime(Interpolation.lerp(
                    this.cameraTransitionPrevious,
                    this.cameraTransition,
                    time));

                break;
            case CameraMode.TRANSITION:
                this.cameraControllerTransition.setInterpolation(Interpolation.lerp(
                    this.cameraTransitionPrevious,
                    this.cameraTransition,
                    time));

                break;
        }

        this.cameraOrbit?.render(time);

        if (this.racers && this.racers.player.ship) {
            const controller = this.cameraController;

            controller.render(time);
            controller.setTarget(controller.targetPlayer ? this.racers.player : this.racers.leader, this.track, time);

            if(this.racers.racers[0].ship !== null) {
                this.environment.lights.setShadowFocus(this.racers.racers[0].ship.position);
            }
            
        }
    }

    /**
     * Add input listeners
     */
    addInputListeners() {
        window.addEventListener("keydown", event => {
            switch (event.key) {
                case "v":
                    switch (this.cameraMode) {
                        case CameraMode.INTRO:
                        case CameraMode.TRANSITION:
                            this.cameraTransitionPrevious = this.cameraTransition = 1;

                            break;
                        case CameraMode.GAME:
                            this.nextCamera();

                            break;
                    }

                    break;
            }
        });
    }

    /**
     * Move to the next camera
     */
    nextCamera() {
        this.cameraIndex = (this.cameraIndex + 1) % this.cameraControllers.length;
    }

    /**
     * Start the intro
     * @param {function} onEndIntro A function to call when the intro has ended
     */
    startIntro(onEndIntro) {
        this.onEndIntro = onEndIntro;
        this.cameraMode = CameraMode.INTRO;
    }

    /**
     * End the intro mode
     */
    endIntro() {
        this.intro = false;
    }

    /**
     * Set the race to ended
     */
    setEnded() {
        this.ended = true;
    }

    /**
     * Set a different camera type
     * @param {string} name The name of the camera type
     */
    userCameraSelection(name) {
        if ((name === 'Chase') || (name === 'chase')) {
            this.cameraTransitionPrevious = this.cameraTransition = 1;
            this.cameraIndex = 1;
        } else if((name === 'Leader') || (name === 'leader')) {
            this.cameraTransitionPrevious = this.cameraTransition = 2;
            this.cameraIndex = 2;
        } else
            this.cameraIndex = 0;
    }

    /**
     * Get the current camera controller
     * @returns {CameraController} The current camera controller
     */
    get cameraController() {
        if (this.intro) {
            switch (this.cameraMode) {
                case CameraMode.INTRO:
                    return this.cameraControllerIntroduction;
                case CameraMode.TRANSITION:
                case CameraMode.GAME:
                    return this.cameraControllerTransition;
                default:
                    return this.cameraStart;
            }
        }

        if (this.racers.first.moved === -1 || this.racers.first.moved > this.track.length * this.track.laps - this.cameraFinish.distance) {
            if (this.ended)
                return this.cameraLeaderboard;

            return this.cameraFinish;
        }

        return this.cameraControllers[this.cameraIndex];
    }
}